import { X } from "lucide-react";
import { useT } from "@/lib/i18n";

type CheckpointTopBarProps = {
  scenarioTitle: string;
  currentIndex: number;
  totalItems: number;
  onClose: () => void;
};

/**
 * Sibling of `SessionTopBar` — same chrome amber palette per doctrine
 * §8.3, with "чекпоинт" label instead of "сценарий". Progress bar
 * tracks question index. Exit prompts the user (handled by caller).
 *
 * Mockup: `design/hifi/scenarios-extra.jsx` `CheckpointQuizScreen`
 * lines ~248–321.
 */
export function CheckpointTopBar({ scenarioTitle, currentIndex, totalItems, onClose }: CheckpointTopBarProps) {
  const t = useT();
  const safeTotal = Math.max(1, totalItems);
  const pct = Math.min(1, Math.max(0, (currentIndex + 1) / safeTotal));
  return (
    <div className="flex flex-col gap-2 pt-1">
      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-label="exit"
          onClick={onClose}
          className="text-text-2 transition-colors hover:text-text"
        >
          <X className="size-6" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-semibold text-text">{scenarioTitle}</div>
          <div className="font-mono text-[11px] uppercase tracking-wider text-text-3">
            {t("skills.checkpoint_label")} ·{" "}
            {t("skills.checkpoint_progress", {
              idx: String(currentIndex + 1),
              total: String(safeTotal),
            })}
          </div>
        </div>
      </div>
      <div
        className="h-[3px] w-full overflow-hidden rounded-full"
        style={{ background: "var(--surface-2)" }}
        role="progressbar"
        aria-valuenow={Math.round(pct * 100)}
      >
        <div
          className="h-full rounded-full transition-[width] duration-300"
          style={{ width: `${pct * 100}%`, background: "var(--scen-solid)" }}
        />
      </div>
    </div>
  );
}
